// MST
import { types, flow, detach, cast } from 'mobx-state-tree'

// Interfaces
import {
  IAssessmentAnotherDocumentGetListAttrs,
  IAssessmentAnotherDocumentDownloadDocumentAttrs,
  IAssessmentAnotherDocumentCreateAttrs,
  IAssessmentAnotherDocumentGetDetailAttrs,
  IAssessmentAnotherDocumentUpdateAttrs,
  IAssessmentAnotherDocumentDeleteAttrs,
  IAssessmentAnotherDocumentUploadDocumentAttrs,
} from 'interfaces/back-office/assessment/another-document/another-document-view.interface'
import {
  IAssessmentAnotherDocumentApiListResponse,
  IAssessmentAnotherDocumentApiDownloadDocumentResponse,
  IAssessmentAnotherDocumentApiDetailResponse,
  IAssessmentAnotherDocumentApiUploadDocumentResponse,
} from 'interfaces/back-office/assessment/another-document/another-document-api.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

// Utils
import { withHelper_fileDownload, withHelper_paginationProps } from 'models/extensions/with-helper'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading, withHelper_fileUserProps } from 'models/extensions/helper'

// Constant
import { LOADING } from 'constant/shared.constant'
import contentDisposition from 'content-disposition'

// Types
const { maybeNull, array, optional, model, number, string } = types

// Loading
const { BASE, DETAIL, ACTION, DOWNLOAD } = LOADING

const anotherDocumentProps = {
  id: maybeNull(number),
  documentName: maybeNull(string),
  loading: withHelper_commonLoadingModel,
  ...withHelper_fileUserProps,
}

const AssessmentAnotherDocument = optional(
  array(
    model('AssessmentAnotherDocumentList').props({
      number: maybeNull(number),
      ...anotherDocumentProps,
    }),
  ),
  [],
)

const AssessmentAnotherDocumentDetail = optional(model('AssessmentAnotherDocumentDetail').props(anotherDocumentProps), {})

const AssessmentAnotherDocumentPaginated = optional(
  model('AssessmentAnotherDocumentPaginated').props({ ...withHelper_paginationProps, list: AssessmentAnotherDocument }),
  {},
)

// Base Model
const AssessmentAnotherDocumentStoreModel = model('AssessmentAnotherDocument')
  .props({
    assessmentAnotherDocument_loading: withHelper_commonLoadingModel,
    assessmentAnotherDocument_list: AssessmentAnotherDocumentPaginated,
    assessmentAnotherDocument_detail: AssessmentAnotherDocumentDetail,
    assessmentAnotherDocument_document_name_list: optional(array(string),[])
  })
  .actions(self => ({
    assessmentAnotherDocument_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.assessmentAnotherDocument_loading, payload)

      detach(self.assessmentAnotherDocument_loading)
      self.assessmentAnotherDocument_loading = cast(map)
    },
    assessmentAnotherDocument_SET_LIST: (payload: IAssessmentAnotherDocumentApiListResponse['res']['data']): void => {
      const map = {
        ...payload,
        list: payload.list.map((list, index) => ({
          number: commonUtils_renderTableNumber(index, payload),
          ...list,
        })),
      }

      detach(self.assessmentAnotherDocument_list)
      self.assessmentAnotherDocument_list = cast(cloneDeep(map))
    },
    assessmentAnotherDocument_SET_DETAIL: (payload: IAssessmentAnotherDocumentApiDetailResponse['res']['data']): void => {
      self.assessmentAnotherDocument_detail = cast(payload)
    },

    assessmentAnotherDocument_SET_DOCUMENT_NAME_LIST: (payload: string[]): void=>{
      self.assessmentAnotherDocument_document_name_list = cast(payload)
    },

    assessmentAnotherDocument_MAP_USER_FILE_LOADING: (payload: ISharedCommonLoadingAttrs & { isList?: boolean }): void => {
      let map: any
      if (payload.isList) {
        map = cloneDeep({
          ...self.assessmentAnotherDocument_list,
          list: self.assessmentAnotherDocument_list.list.map(list => {
            return {
              ...list,
              loading:
                list.id === payload.id || list.userFileBlobId === payload.id
                  ? withHelper_commonMapLoading(list.loading, payload)
                  : list.loading,
            }
          }),
        })
      } else {
        map = cloneDeep({
          ...self.assessmentAnotherDocument_detail,
          loading:
            self.assessmentAnotherDocument_detail.id === payload.id ||
            self.assessmentAnotherDocument_detail.userFileBlobId === payload.id
              ? withHelper_commonMapLoading(self.assessmentAnotherDocument_detail.loading, payload)
              : self.assessmentAnotherDocument_detail.loading,
        })
      }

      if (payload?.isList) {
        detach(self.assessmentAnotherDocument_list)
        self.assessmentAnotherDocument_list = cast(map)
      } else {
        detach(self.assessmentAnotherDocument_detail)
        self.assessmentAnotherDocument_detail = cast(map)
      }
    },
    assessmentAnotherDocument_MAP_USER_FILE_DETAIL: (
      payload: IAssessmentAnotherDocumentApiUploadDocumentResponse['res']['data'],
    ): void => {
      self.assessmentAnotherDocument_detail = cast(
        cloneDeep({
          ...self.assessmentAnotherDocument_detail,
          ...payload,
        }),
      )
    },
    assessmentAnotherDocument_CLEAR_LIST: (): void => {
      self.assessmentAnotherDocument_list = cast({})
    },
    assessmentAnotherDocument_CLEAR_DETAIL: (): void => {
      self.assessmentAnotherDocument_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment another document list
     *
     * @param {IAssessmentAnotherDocumentGetListAttrs} payload
     *
     * @return {any} any
     */
    assessmentAnotherDocument_fetchList: flow(function* (payload: IAssessmentAnotherDocumentGetListAttrs) {
      self.assessmentAnotherDocument_SET_LOADING({ type: BASE, value: true })

      const response: IAssessmentAnotherDocumentApiListResponse =
        yield self.environment.assessmentAnotherDocumentApi.assessmentAnotherDocument_getList({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        console.log("ini set list",response.res.data);
        self.assessmentAnotherDocument_SET_LIST(response.res.data)
        let documentNames:string[] = [] 
        response.res.data.list.forEach(el => documentNames.push(el.documentName || ""))
        self.assessmentAnotherDocument_SET_DOCUMENT_NAME_LIST(documentNames)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentAnotherDocument_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Create assessment another document
     *
     * @param {IAssessmentAnotherDocumentCreateAttrs} payload
     *
     * @return {any} any
     */
    assessmentAnotherDocument_create: flow(function* (payload: IAssessmentAnotherDocumentCreateAttrs) {
      self.assessmentAnotherDocument_SET_LOADING({ type: ACTION, value: true })

      const response: IAssessmentAnotherDocumentApiDetailResponse =
        yield self.environment.assessmentAnotherDocumentApi.assessmentAnotherDocument_create({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        // When success, map detail to data according the new created another document.
        self.assessmentAnotherDocument_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentAnotherDocument_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Fetch assessment another document detail
     *
     * @param {IAssessmentAnotherDocumentGetDetailAttrs} payload
     *
     * @return {any} any
     */
    assessmentAnotherDocument_fetchDetail: flow(function* (payload: IAssessmentAnotherDocumentGetDetailAttrs) {
      self.assessmentAnotherDocument_SET_LOADING({ type: DETAIL, value: true })

      const response: IAssessmentAnotherDocumentApiDetailResponse =
        yield self.environment.assessmentAnotherDocumentApi.assessmentAnotherDocument_getDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        self.assessmentAnotherDocument_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentAnotherDocument_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Update assessment another document
     *
     * @param {IAssessmentAnotherDocumentUpdateAttrs} payload
     *
     * @return {any} any
     */
    assessmentAnotherDocument_update: flow(function* (payload: IAssessmentAnotherDocumentUpdateAttrs) {
      self.assessmentAnotherDocument_SET_LOADING({ type: ACTION, value: true })

      const response: IAssessmentAnotherDocumentApiDetailResponse =
        yield self.environment.assessmentAnotherDocumentApi.assessmentAnotherDocument_update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentAnotherDocument_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Delete assessment another document
     *
     * @param {IAssessmentAnotherDocumentDeleteAttrs} payload
     *
     * @return {any} any
     */
    assessmentAnotherDocument_delete: flow(function* (payload: IAssessmentAnotherDocumentDeleteAttrs) {
      self.assessmentAnotherDocument_SET_LOADING({ type: ACTION, value: true })

      const response: IAssessmentAnotherDocumentApiDetailResponse =
        yield self.environment.assessmentAnotherDocumentApi.assessmentAnotherDocument_delete({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentAnotherDocument_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Upload Document of another document
     *
     * @param {IAssessmentAnotherDocumentUploadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentAnotherDocument_uploadDocument: flow(function* (payload: IAssessmentAnotherDocumentUploadDocumentAttrs) {
      const { anotherDocumentId } = payload.params

      self.assessmentAnotherDocument_MAP_USER_FILE_LOADING({ id: anotherDocumentId, type: ACTION, value: true })

      const response: IAssessmentAnotherDocumentApiUploadDocumentResponse =
        yield self.environment.assessmentAnotherDocumentApi.assessmentAnotherDocument_uploadDocument({
          params: { anotherDocumentId },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        self.assessmentAnotherDocument_MAP_USER_FILE_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentAnotherDocument_MAP_USER_FILE_LOADING({ id: anotherDocumentId, type: ACTION, value: false })
    }),

    /**
     * @description Download assessment another document, document
     *
     * @param {IAssessmentAnotherDocumentDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentAnotherDocument_downloadDocument: flow(function* (payload: IAssessmentAnotherDocumentDownloadDocumentAttrs) {
      const { blobId, isList } = payload.params

      self.assessmentAnotherDocument_MAP_USER_FILE_LOADING({ id: blobId, type: DOWNLOAD, value: true, isList })

      const response: IAssessmentAnotherDocumentApiDownloadDocumentResponse =
        yield self.environment.assessmentAnotherDocumentApi.assessmentAnotherDocument_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        const _contentDisposition = response.headers?.["content-disposition"] || ""
        const filename = contentDisposition.parse(_contentDisposition).parameters?.filename || "";
        const fileType = filename.split(".")[filename.split(".").length - 1];
        let mimeType = "";

        if (fileType === "pdf") {
          mimeType = 'application/pdf';
        } else if (fileType === "jpg" || fileType === "jpeg" || fileType === "png") {
          mimeType = `image/${fileType}`;
        } else {
          withHelper_fileDownload(response.res, response.headers!);
          // Do when response is success
          if (payload?.onSuccess) payload.onSuccess() ;
          return;
        }
        
        const file = new File([response.res], filename, {
          type: mimeType
        });
        const url = URL.createObjectURL(file);
        window.open(url);

        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentAnotherDocument_MAP_USER_FILE_LOADING({ id: blobId, type: DOWNLOAD, value: false, isList })
    }),
  }))

export const createAssessmentAnotherDocumentStoreDefaultModel = () => optional(AssessmentAnotherDocumentStoreModel, {})
